import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ExternalLink, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";

// Guidance content database
const guidanceContent: Record<string, {
  title: string;
  description: string;
  sections: Array<{
    heading: string;
    content: string;
    list?: string[];
    links?: Array<{ text: string; url: string }>;
    type?: "info" | "warning" | "success";
  }>;
}> = {
  "after-12th": {
    title: "What can I do after 12th for free?",
    description: "Complete guide to free education and skill options after completing higher secondary.",
    sections: [
      {
        heading: "Free Government Skill Programs",
        content: "The Government of India offers several fully-funded skill development programs:",
        list: [
          "PMKVY (Pradhan Mantri Kaushal Vikas Yojana) - Free training in 40+ sectors",
          "NAPS (National Apprenticeship Promotion Scheme) - Earn ₹5,000-9,000/month while learning",
          "Skill India Digital Hub - Single portal for all government schemes",
          "NIELIT Courses - Free computer and IT training",
        ],
        links: [
          { text: "Skill India Digital", url: "https://skillindiadigital.gov.in" },
          { text: "PMKVY Portal (Skill India Digital Hub)", url: "https://www.skillindiadigital.gov.in/pmkvy-landing" },
        ],
      },
      {
        heading: "Free Online Learning Platforms",
        content: "World-class education is available for free online:",
        list: [
          "SWAYAM - 2000+ free courses from UGC, AICTE, IITs",
          "NPTEL - Engineering and science courses from IITs",
          "Coursera Audit Mode - Learn for free, pay only for certificate",
          "FreeCodeCamp - Complete web development for free",
          "Khan Academy - Math, Science, Economics - all free",
        ],
        links: [
          { text: "SWAYAM", url: "https://swayam.gov.in" },
          { text: "FreeCodeCamp", url: "https://www.freecodecamp.org" },
        ],
      },
      {
        heading: "Free Tech Certifications",
        content: "Global tech companies offer free courses with certificates:",
        list: [
          "Google Skillshop - Digital Marketing (Free certificate)",
          "Microsoft Learn - Excel, Azure, AI pathways",
          "IBM SkillsBuild - AI, Data Science, Cybersecurity",
          "AWS Training - Cloud computing fundamentals",
        ],
        type: "success",
      },
      {
        heading: "Important Tips",
        content: "",
        list: [
          "Start with Skill India Digital to explore all government options",
          "For tech careers, begin with FreeCodeCamp or Google courses",
          "Keep your 10th and 12th marksheets ready for registrations",
          "Never pay for \"job-guaranteed\" courses - legitimate programs are free",
        ],
        type: "info",
      },
    ],
  },
  "apprenticeships": {
    title: "Understanding Apprenticeships",
    description: "Earn while you learn - everything about NAPS and apprenticeship programs in India.",
    sections: [
      {
        heading: "What is an Apprenticeship?",
        content: "An apprenticeship is a work-based training program where you learn skills on the job while earning a monthly stipend. Unlike classroom training, you work in real companies and gain practical experience.",
      },
      {
        heading: "NAPS - National Apprenticeship Promotion Scheme",
        content: "NAPS is the government's flagship apprenticeship program:",
        list: [
          "Monthly stipend: ₹5,000 to ₹9,000 depending on qualification",
          "Duration: 6 months to 3 years",
          "Available in: Manufacturing, IT, Healthcare, Retail, and more",
          "Age: 14+ years (varies by trade)",
          "No fees - you get paid to learn",
        ],
        links: [
          { text: "Register on Apprenticeship India", url: "https://www.apprenticeshipindia.gov.in" },
        ],
        type: "success",
      },
      {
        heading: "How to Apply",
        content: "",
        list: [
          "Create an account on apprenticeshipindia.gov.in",
          "Complete your profile with education and documents",
          "Search for apprenticeship opportunities by location/trade",
          "Apply directly to companies offering apprenticeships",
          "Attend interview if shortlisted",
        ],
      },
      {
        heading: "Benefits of Apprenticeship",
        content: "",
        list: [
          "Earn money while learning - no college fees",
          "Real industry experience — often valued by employers",
          "Higher job readiness compared to classroom-only training",
          "Government certificate after completion",
          "Many apprentices get absorbed by the same company",
        ],
        type: "info",
      },
    ],
  },
  "avoid-scams": {
    title: "How to Avoid Fake Courses & Scams",
    description: "Red flags to watch for and how to verify if a program is legitimate.",
    sections: [
      {
        heading: "🚨 Major Red Flags",
        content: "Never trust any \"course\" or \"job\" that:",
        list: [
          "Asks for registration fee for jobs (Real companies NEVER charge)",
          "Guarantees job placement after paying money",
          "Asks for \"processing fee\" or \"training fee\" for government schemes",
          "Contacts you on WhatsApp about jobs you didn't apply for",
          "Asks for Aadhaar/PAN card photos via WhatsApp",
          "Promises ₹50,000+ salary for freshers without interview",
        ],
        type: "warning",
      },
      {
        heading: "How to Verify a Program",
        content: "Before enrolling in any program:",
        list: [
          "Check if it's listed on Skill India Digital (skillindiadigital.gov.in)",
          "Verify the training center on PMKVY official website",
          "Search for reviews on Google with \"[program name] + scam\"",
          "Call the official helpline numbers from government websites",
          "Never click links from unknown WhatsApp forwards",
        ],
      },
      {
        heading: "Government Schemes are FREE",
        content: "Remember: Genuine government skill programs like PMKVY, NAPS, and NIELIT are designed to be free. If someone asks you to pay fees for these programs, exercise caution.",
        type: "info",
      },
      {
        heading: "Report Scams",
        content: "If you encounter a scam:",
        list: [
          "Report on cybercrime.gov.in",
          "Call 1930 (Cyber Crime Helpline)",
          "File complaint at nearest police station",
          "Warn others in your community",
        ],
        links: [
          { text: "Cyber Crime Portal", url: "https://cybercrime.gov.in" },
        ],
      },
    ],
  },
  "how-to-apply": {
    title: "How to Apply Step-by-Step",
    description: "A simple guide to applying for free government and online programs.",
    sections: [
      {
        heading: "Documents You'll Need",
        content: "Keep these ready before starting any application:",
        list: [
          "10th Marksheet (original + photocopy)",
          "12th Marksheet (original + photocopy)",
          "Aadhaar Card",
          "Passport size photos (white background)",
          "Bank account details (for stipend programs)",
          "Active mobile number linked to Aadhaar",
          "Email ID (create one if you don't have)",
        ],
        type: "info",
      },
      {
        heading: "Step 1: Create Accounts",
        content: "Register on these key platforms:",
        list: [
          "Skill India Digital (skillindiadigital.gov.in) - for government programs",
          "SWAYAM (swayam.gov.in) - for online courses",
          "Apprenticeship India (apprenticeshipindia.gov.in) - for apprenticeships",
        ],
        links: [
          { text: "Skill India Digital", url: "https://skillindiadigital.gov.in" },
        ],
      },
      {
        heading: "Step 2: Complete Your Profile",
        content: "",
        list: [
          "Fill all personal details accurately",
          "Upload clear photos of documents",
          "Verify your mobile number via OTP",
          "Add your education qualification correctly",
        ],
      },
      {
        heading: "Step 3: Search & Apply",
        content: "",
        list: [
          "Browse available programs by category or location",
          "Read eligibility criteria carefully",
          "Check the application deadline",
          "Click Apply and follow the instructions",
          "Save/screenshot your application number",
        ],
      },
      {
        heading: "Step 4: Track Your Application",
        content: "",
        list: [
          "Login regularly to check status",
          "Respond to any calls from training centers",
          "Keep your phone reachable",
          "Attend assessments on scheduled dates",
        ],
        type: "success",
      },
    ],
  },
  "skill-vs-apprenticeship": {
    title: "Skill Training vs Apprenticeship",
    description: "Understand the difference and choose what's right for you.",
    sections: [
      {
        heading: "Skill Training (PMKVY)",
        content: "Classroom-based training at a training center:",
        list: [
          "Duration: 3-6 months typically",
          "Location: Training center near your area",
          "Payment: You don't earn (training is free)",
          "Learning: Classroom + some practical",
          "Certificate: National Skill Qualification Certificate",
          "Best for: Those who want structured learning first",
        ],
      },
      {
        heading: "Apprenticeship (NAPS)",
        content: "On-the-job training at a company:",
        list: [
          "Duration: 6 months to 3 years",
          "Location: At a company/factory/office",
          "Payment: ₹5,000-9,000 monthly stipend",
          "Learning: 80% practical, 20% classroom",
          "Certificate: National Apprenticeship Certificate",
          "Best for: Those who want to earn while learning",
        ],
        type: "success",
      },
      {
        heading: "Which Should You Choose?",
        content: "",
        list: [
          "Choose Skill Training if: You need basic skills first, prefer classroom learning, or can't commit to full-time work yet",
          "Choose Apprenticeship if: You want to earn money, prefer learning by doing, and are ready for a work environment",
          "You can do both: Complete skill training first, then apply for apprenticeship with better skills",
        ],
        type: "info",
      },
      {
        heading: "Quick Comparison",
        content: "| Feature | Skill Training | Apprenticeship |\n|---------|----------------|----------------|\n| Duration | 3-6 months | 6-36 months |\n| Stipend | No | Yes (₹5,000-9,000) |\n| Location | Training center | Company |\n| Learning | 60% classroom | 80% on-job |",
      },
    ],
  },
};

const GuidanceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? guidanceContent[slug] : null;

  if (!content) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Guide Not Found</h1>
            <p className="text-muted-foreground mb-6">The guide you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/guidance">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Guidance
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Seo
        title={`${content.title} | we-help.in`}
        description={content.description.slice(0, 155)}
        path={`/guidance/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: content.title,
          description: content.description,
          mainEntityOfPage: `https://we-help.in/guidance/${slug}`,
          publisher: {
            "@type": "Organization",
            name: "we-help.in",
            url: "https://we-help.in",
          },
        }}
      />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-accent/50 to-background py-12 md:py-16">
          <div className="container">
            <Button variant="ghost" asChild className="mb-6">
              <Link to="/guidance">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Guidance
              </Link>
            </Button>
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {content.title}
              </h1>
              <p className="text-lg text-muted-foreground">
                {content.description}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container">
            <div className="max-w-3xl space-y-8">
              {content.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-6 rounded-2xl border ${
                    section.type === "warning" 
                      ? "bg-destructive/5 border-destructive/20" 
                      : section.type === "success"
                      ? "bg-success/5 border-success/20"
                      : section.type === "info"
                      ? "bg-primary/5 border-primary/20"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {section.type === "warning" && (
                      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                    )}
                    {section.type === "success" && (
                      <CheckCircle className="h-5 w-5 text-success shrink-0 mt-0.5" />
                    )}
                    {section.type === "info" && (
                      <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-foreground mb-3">
                        {section.heading}
                      </h2>
                      {section.content && (
                        <p className="text-muted-foreground mb-4 whitespace-pre-wrap">
                          {section.content}
                        </p>
                      )}
                      {section.list && (
                        <ul className="space-y-2 mb-4">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="text-primary mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.links && section.links.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {section.links.map((link, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              asChild
                            >
                              <a href={link.url} target="_blank" rel="noopener noreferrer">
                                {link.text}
                                <ExternalLink className="h-3 w-3 ml-1" />
                              </a>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-muted/30 border-t border-border">
          <div className="container">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">Ready to explore programs?</h2>
              <p className="text-muted-foreground mb-6">
                Browse curated free education and skill development opportunities.
              </p>
              <Button asChild size="lg">
                <Link to="/programs">
                  Explore Free Programs
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GuidanceDetail;
